import {
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { LetterOfSupportInfo } from "./LetterOfSupportInfo";
import { LightBanner } from "./LightBanner";

interface RequirementsProps {
  showHelp?: boolean;
}

function Requirements({ showHelp = false }: RequirementsProps) {
  return (
    <>
      <Box component="ul" m={0} pl={3}>
        <Typography component="li">
          Your child's N number. If you don't have it, please contact
          wellness@inuvialuit.com with your child's full name and date of birth.
        </Typography>
        <Typography component="li">
          A letter of support from a health, social or educational professional
        </Typography>
        <Typography component="li">
          Any other documents that apply — assessments, prescriptions, an IEP,
          or reports
        </Typography>
      </Box>
      <LetterOfSupportInfo />
      {showHelp ? (
        <Typography variant="body1" mt={2}>
          If you have any questions or need help, please contact{" "}
          <a href="mailto:inuitcfi@inuvialuit.com">inuitcfi@inuvialuit.com</a>.
        </Typography>
      ) : null}
    </>
  );
}

function Download() {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      gap={2}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <FileCopyIcon color="error" fontSize="large" />
        <Box>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            Letter of Support Checklist (PDF)
          </Typography>
          <Typography variant="body1">
            Share this with the professional writing your letter so nothing is
            missed
          </Typography>
        </Box>
      </Stack>
      <Button
        component="a"
        href="/documents/Inuit_Child_First_Initiative_Checklist_4.pdf"
        variant="contained"
        download="Inuit_Child_First_Initiative_Checklist.pdf"
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        Download
      </Button>
    </Stack>
  );
}

export interface CfiDocumentGateProps {
  onApply: () => void;
}

function DocumentGate({ onApply }: CfiDocumentGateProps) {
  const [hasDocuments, setHasDocuments] = useState(false);

  return (
    <Box>
      <Box bgcolor="primary.main" color="primary.contrastText" p={2}>
        <Stack>
          <Typography variant="h5">
            What you'll need before you start
          </Typography>
          <Typography variant="body1">
            Applications missing documents take longer to review. Gather these
            first so your child's request can move ahead without delay.
          </Typography>
        </Stack>
        <Stack gap={4} pt={3} pb={0}>
          <Box bgcolor="background.paper" color="text.primary" p={2}>
            <Requirements showHelp />
          </Box>
          <Box bgcolor="background.paper" color="text.primary" p={2}>
            <Download />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasDocuments}
                onChange={(event) => setHasDocuments(event.target.checked)}
                icon={<CheckBoxOutlineBlankIcon sx={{ color: "white" }} />}
                checkedIcon={<CheckBoxIcon sx={{ color: "white" }} />}
                size="large"
              />
            }
            label="I have these documents ready"
          />
        </Stack>
      </Box>
      <Button
        variant="contained"
        disabled={!hasDocuments}
        onClick={() => {
          onApply();
        }}
        size="large"
        sx={{ mt: 3, ml: 1 }}
      >
        Apply Now
      </Button>
    </Box>
  );
}

function ApprovedGate() {
  const [visible, setVisible] = useState(false);

  return (
    <Box>
      <Box>
        <LightBanner
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">
            {" "}
            <CheckCircleIcon
              fontSize="inherit"
              sx={{ verticalAlign: "text-top" }}
            />{" "}
            You've confirmed you have these ready
          </Typography>
          <Button
            variant="text"
            onClick={() => setVisible(!visible)}
            sx={(theme) => ({
              textTransform: "none",
            })}
          >
            <Typography
              variant="body1"
              lineHeight={1}
              sx={(theme) => ({
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              })}
            >
              {visible ? "Hide checklist" : "View checklist"}
            </Typography>
          </Button>
        </LightBanner>
        <Collapse in={visible}>
          <Box p={4} border={1} borderColor="divider">
            <Requirements />
            <Card variant="outlined" sx={{ mt: 4, boxShadow: "none", p: 2 }}>
              <Download />
            </Card>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export function CfiDocumentGate({ onApply }: CfiDocumentGateProps) {
  const [applied, setApplied] = useState(false);

  if (applied) {
    return <ApprovedGate />;
  }

  return (
    <DocumentGate
      onApply={() => {
        setApplied(true);
        onApply();
      }}
    />
  );
}
